import { ErrorInfo, NotFound } from "@bootstrap";
import { useEffect, useState } from "react";
import { isRouteErrorResponse, Navigate, useRouteError } from "react-router";
// Guarded accessor, NOT @posthog/react's usePostHog: this boundary is in the
// entry chunk, and @posthog/react statically imports posthog-js — importing it
// here would pull the SDK back into the entry bundle (SD-10530). Null until
// the lazily-loaded SDK initializes; pre-init captures were dropped anyway
// (uninitialized + opted-out default), so skipping them is behavior-identical.
import { getPostHog } from "@/analytics/posthog";
import { captureClientRouteError } from "@/utils/sentryRouteError";
import { checkAuth } from "./authLoader";
import { ChunkUpdatingNotice } from "./ChunkUpdatingNotice";
import {
  isChunkLoadError,
  isReloadThrottled,
  recoverFromChunkLoadError,
} from "./chunkLoadRecovery";
import {
  CHUNK_RECOVERY_EVENT,
  collectChunkRecoveryProps,
} from "./chunkRecoveryTelemetry";
import { isPublicRoute } from "./routeConfig";

export const RootErrorBoundary = () => {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : "";
  const chunkError = isChunkLoadError(message);
  // True once recovery is exhausted (a reload was already triggered within the
  // throttle window, or the effect's reload attempt was throttled) — fall back
  // to the real error instead of a stuck "updating" state. Seeded from the
  // pure throttle check so an already-exhausted chunk error renders the real
  // error immediately instead of flashing "Updating…" for one frame.
  const [recoveryExhausted, setRecoveryExhausted] = useState(
    () => chunkError && isReloadThrottled(),
  );

  useEffect(() => {
    if (!checkAuth() && !isPublicRoute(window.location.pathname)) {
      return;
    }
    if (chunkError) {
      // Stale code-split chunk (older tab vs. newer deploy) — expected, not a
      // bug. Recover by reloading; only report if recovery is exhausted. The
      // attempt itself is measured either way: send_instantly because a
      // successful recovery has already queued the reload.
      const recovered = recoverFromChunkLoadError();
      const recoveryProps = collectChunkRecoveryProps(
        "root",
        recovered ? "reload" : "exhausted",
        message,
      );
      getPostHog()?.capture(CHUNK_RECOVERY_EVENT, recoveryProps, {
        send_instantly: true,
      });
      if (!recovered) {
        setRecoveryExhausted(true);
        captureClientRouteError(error, recoveryProps);
        // Recovery exhausted = a real stuck-user error, not transient deploy
        // churn. Report to PostHog too for Sentry/PostHog parity.
        getPostHog()?.captureException(error);
      }
      return;
    }
    if (isRouteErrorResponse(error)) {
      if (error.status === 401 || error.status === 404) {
        return;
      }
    }
    captureClientRouteError(error);
    getPostHog()?.captureException(error);
  }, [error, chunkError, message]);

  // Only redirect to /login if we're NOT already on a public route.
  // Without this guard, a crash on /login itself causes an infinite
  // redirect loop (login errors → checkAuth false → redirect /login → repeat).
  if (!checkAuth() && !isPublicRoute(window.location.pathname)) {
    return <Navigate to="/login" replace />;
  }

  // Stale chunk: show a transient updating state while the reload lands the
  // user on the route they navigated to.
  if (chunkError && !recoveryExhausted) {
    return <ChunkUpdatingNotice fullScreen />;
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 401) {
      return <Navigate to="/login" replace />;
    }
    if (error.status === 404) {
      return <NotFound />;
    }
  }

  return (
    <main className="flex h-screen w-full items-center justify-center p-8">
      <ErrorInfo
        title="Uh-oh!"
        subtitle="Something went wrong"
        description="We are working to fix this issue"
      />
    </main>
  );
};
